import { useState, useCallback, useRef, useEffect } from "react";
import { motion } from "framer-motion";
import { useTheme } from "next-themes";
import {
  Plus, X, LayoutDashboard, Sun, Moon, LogOut, FileText, KeyRound, Plug,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/context/AuthContext";
import { api, apiErr } from "@/lib/api";
import Dashboard from "@/components/Dashboard";
import PdfReader from "@/components/PdfReader";
import IntegrationsDialog from "@/components/Integrations";
import SearchBar from "@/components/SearchBar";
import BackgroundPicker from "@/components/BackgroundPicker";

let counter = 1;
const newId = () => `t${counter++}`;

function ChangePasswordDialog({ open, onClose }) {
  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setErr("");
    if (next !== confirm) return setErr("New passwords do not match");
    if (next.length < 6) return setErr("New password must be at least 6 characters");
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: cur, new_password: next });
      toast.success("Password changed");
      setCur(""); setNext(""); setConfirm("");
      onClose();
    } catch (e) {
      setErr(apiErr(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader><DialogTitle className="font-head">Change password</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Current password</Label><Input data-testid="cur-password" type="password" value={cur} onChange={(e) => setCur(e.target.value)} className="mt-1" /></div>
          <div><Label>New password</Label><Input data-testid="new-password" type="password" value={next} onChange={(e) => setNext(e.target.value)} className="mt-1" /></div>
          <div><Label>Confirm new password</Label><Input data-testid="confirm-password" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} className="mt-1" /></div>
          {err && <p className="text-sm text-red-600" data-testid="pw-error">{err}</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button data-testid="pw-submit" onClick={submit} disabled={busy}>{busy ? "Saving…" : "Update password"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Workspace() {
  const { user, logout } = useAuth();
  const { theme, setTheme } = useTheme();
  const [tabs, setTabs] = useState([{ id: newId(), type: "dashboard", title: "Library" }]);
  const [active, setActive] = useState(null);
  const [pwOpen, setPwOpen] = useState(false);
  const [intOpen, setIntOpen] = useState(false);
  const tabsRef = useRef(tabs);
  useEffect(() => { tabsRef.current = tabs; }, [tabs]);

  const activeId = active || tabs[0].id;

  const focusOrOpen = useCallback((tab) => {
    const current = tabsRef.current;
    if (tab.type === "book") {
      const existing = current.find((t) => t.type === "book" && t.bookId === tab.bookId);
      if (existing) {
        setActive(existing.id);
        return;
      }
    }
    const id = newId();
    const next = [...current, { id, ...tab }];
    tabsRef.current = next;
    setTabs(next);
    setActive(id);
  }, []);

  const openBook = useCallback(
    (book, page) => focusOrOpen({ type: "book", bookId: book.id, title: book.title, page }),
    [focusOrOpen]
  );

  const addDashboardTab = () => focusOrOpen({ type: "dashboard", title: "Library" });

  const closeTab = (id, e) => {
    e?.stopPropagation();
    setTabs((prev) => {
      const next = prev.filter((t) => t.id !== id);
      if (next.length === 0) return prev;
      if (id === activeId) setActive(next[next.length - 1].id);
      return next;
    });
  };

  return (
    <div className="h-screen flex flex-col bg-slate-50 dark:bg-slate-950 overflow-hidden">
      {/* Top bar */}
      <header className="flex items-center justify-between px-4 h-12 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 shrink-0">
        <div className="flex items-center gap-2 font-head font-bold text-slate-900 dark:text-slate-50">
          <img src="/exlibris-logo.jpg" alt="Ex Libris" className="h-7 w-7 rounded-full object-cover ring-1 ring-slate-200 dark:ring-slate-700" /> Ex Libris
        </div>
        <div className="flex items-center gap-2">
          <Button data-testid="theme-toggle" variant="ghost" size="icon"
            onClick={() => setTheme(theme === "dark" ? "light" : "dark")}>
            {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button data-testid="user-menu" variant="ghost" size="sm" className="gap-2">
                <span className="h-6 w-6 rounded-full bg-indigo-600 text-white grid place-items-center text-xs font-bold">
                  {user?.name?.[0]?.toUpperCase() || "U"}
                </span>
                <span className="hidden sm:inline">{user?.name}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <div className="px-2 py-1.5 text-xs text-muted-foreground">
                {user?.email} · {user?.role}
              </div>
              <DropdownMenuItem data-testid="change-password-btn" onClick={() => setPwOpen(true)}>
                <KeyRound className="h-4 w-4 mr-2" /> Change password
              </DropdownMenuItem>
              <DropdownMenuItem data-testid="integrations-btn" onClick={() => setIntOpen(true)}>
                <Plug className="h-4 w-4 mr-2" /> Integrations
              </DropdownMenuItem>
              <DropdownMenuItem data-testid="logout-btn" onClick={logout}>
                <LogOut className="h-4 w-4 mr-2" /> Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      <ChangePasswordDialog open={pwOpen} onClose={() => setPwOpen(false)} />
      <IntegrationsDialog open={intOpen} onClose={() => setIntOpen(false)} />

      {/* Tab strip */}
      <div className="flex items-stretch h-10 bg-slate-100 dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 shrink-0">
        <div className="flex items-stretch flex-1 min-w-0 overflow-x-auto no-scrollbar">
          {tabs.map((t) => (
            <div
              key={t.id}
              data-testid={`tab-${t.type}`}
              onClick={() => setActive(t.id)}
              className={`group flex items-center gap-2 px-4 max-w-[220px] cursor-pointer border-r border-slate-200 dark:border-slate-800 text-sm whitespace-nowrap transition-colors ${
                t.id === activeId
                  ? "bg-white dark:bg-slate-950 border-t-2 border-t-indigo-600 text-slate-900 dark:text-slate-50"
                  : "text-slate-500 hover:bg-slate-200/60 dark:hover:bg-slate-800"
              }`}
            >
              {t.type === "dashboard" ? <LayoutDashboard className="h-3.5 w-3.5" /> : <FileText className="h-3.5 w-3.5" />}
              <span className="truncate">{t.title}</span>
              {tabs.length > 1 && (
                <button
                  data-testid="tab-close"
                  onClick={(e) => closeTab(t.id, e)}
                  className="opacity-0 group-hover:opacity-100 hover:text-red-500"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          ))}
          <button
            data-testid="new-tab-btn"
            onClick={addDashboardTab}
            className="px-3 text-slate-500 hover:text-indigo-600 hover:bg-slate-200/60 dark:hover:bg-slate-800 transition-colors"
            title="New tab"
          >
            <Plus className="h-4 w-4" />
          </button>
        </div>
        <SearchBar openBook={openBook} />
      </div>

      {/* Tab panels */}
      <div className="flex-1 min-h-0 relative">
        {tabs.map((t) => (
          <div key={t.id} className={`absolute inset-0 ${t.id === activeId ? "block" : "hidden"}`}>
            {t.type === "dashboard" ? (
              <Dashboard openBook={openBook} />
            ) : (
              <PdfReader bookId={t.bookId} initialPage={t.page} />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
